import outgoingPostMessageUtils from '@/utils/eventCommunicationUtils/outgoingPostMessageUtils';
import waitForStoreGetter from '@/utils/waitForStoreGetter/waitForStoreGetter';
import logError from './logError/logError';

// NavigationUtils - handles navigation between POV and Angular
// Module state
let router;
let store;
let unsubscribe;

/**
 * Initialize the navigation utility with the Vue router and store
 * @param {Object} importedRouter - The Vue router instance
 * @param {Object} importedStore - The Vuex store instance
 */
export const initNavigationUtil = (importedRouter, importedStore) => {
  router = importedRouter;
  store = importedStore;
};

export const getRouter = () => router;

/**
 * Helper function to wait for Angular to load before navigating
 * @param {Object} payload - The navigation payload to use after Angular loads
 * @param {Function} callback - The navigation function to call once Angular is loaded
 * @private
 */
export const waitForAngularToLoad = async (payload, callback) => {
  const requestedFromPath = router.currentRoute.path;
  try {
    await waitForStoreGetter(store, 'AngularStore/isAngularLoaded', 50000);
    // The page that queued this navigation has been left; firing it would drag POV back to it.
    if (router.currentRoute.path !== requestedFromPath) return;
    callback(payload);
  } catch (error) {
    logError('Failed while waiting for Angular to load', error, { payload });
  }
};

/**
 * Splits embedded query parameters out of a path string.
 * Menu items may store query params inside the path (e.g. "/app/my-livesite?section=client-portal")
 * instead of as a separate queryParams object. This extracts them so Angular and POV routers
 * receive a clean path and a proper queryParams object.
 * Explicit queryParams take precedence over embedded ones when keys overlap.
 *
 * @param {string} path - The navigation path, possibly containing a query string
 * @param {Object} [queryParams] - Explicit query parameters passed alongside the path
 * @returns {{ cleanPath: string, mergedQueryParams: Object|undefined }}
 */
const extractQueryParams = (path, queryParams) => {
  const queryIndex = path.indexOf('?');
  if (queryIndex === -1) return { cleanPath: path, mergedQueryParams: queryParams };

  const embeddedParams = Object.fromEntries(new URLSearchParams(path.substring(queryIndex)));
  return {
    cleanPath: path.substring(0, queryIndex),
    mergedQueryParams: { ...embeddedParams, ...queryParams },
  };
};

const handleAngularPath = (path, queryParams) => {
  let angularPath = path;
  if (!path.startsWith('/app/')) {
    angularPath = '/app' + (path.startsWith('/') ? '' : '/') + path;
  }
  outgoingPostMessageUtils.post('deep-link', {path: angularPath, queryParams});
};

const getRouteFullPath = (route) => route?.fullPath || route?.path;

const isCurrentRoute = (location, resolvedRoute) => {
  const targetRoute = resolvedRoute || router.resolve(location).route;
  return getRouteFullPath(targetRoute) === getRouteFullPath(router.currentRoute);
};

const pushIfRouteChanged = (location, resolvedRoute) => {
  if (isCurrentRoute(location, resolvedRoute)) return;
  router.push(location).catch(() => undefined);
};

/**
 * Unified navigation handler that manages routing between POV (Vue) and Angular
 * parts of the application.
 *
 * @param {Object} payload - Navigation payload
 * @param {string} [payload.name] - Route name for POV navigation (always uses Vue router)
 * @param {string} [payload.path] - Path to navigate to if angular uses deep-link (can lead to Angular or POV)
 *                                 If path starts with '/app/', it's normalized for POV
 * @param {string} [payload.route] - Angular route name (typically starts with 'app.')
 * @param {Object} [payload.params] - Route params for named routes (POV only)
 * @param {Object} [payload.queryParams] - Query parameters to pass in the navigation
 * @returns {void}
 */
export default function handleNavigation(payload) {
  // Validate dependencies
  if (!router) {
    console.error('Router not available for navigation. Make sure initNavigationUtil is called with router.');
    return;
  }
  if (!store) {
    console.error('Store not available for navigation. Make sure initNavigationUtil is called with store.');
    return;
  }

  // Cancel any pending navigation subscription
  if (unsubscribe) unsubscribe();

  const { name, path, route, params, queryParams, options } = payload;

  // Always reset modal mode before navigation
  store.dispatch('ModalStore/setForceModalMode', false);

  // Case 1: Name-based navigation - always goes to POV
  if (name) {
    pushIfRouteChanged({ name, params, query: queryParams });
    store.dispatch('AngularStore/setIsNavigatingToAngular', false);
    return;
  }

  // Case 2: Route-based navigation - always goes to Angular
  if (route) {
    // Wait for Angular if needed
    if (!store.getters['AngularStore/isAngularLoaded']) {
      waitForAngularToLoad(payload, handleNavigation);
      return;
    }

    outgoingPostMessageUtils.post('navigate-to', {
      route,
      data: queryParams,
      options,
    });
    return;
  }

  // Case 3: Path-based navigation - could be Angular or POV
  if (path) {
    const { cleanPath, mergedQueryParams } = extractQueryParams(path, queryParams);

    const normalizedPath = cleanPath.startsWith('/app/') ? cleanPath.replace('/app', '') : cleanPath;

    const resolvedRoute = router.resolve(normalizedPath);
    const isAngularRoute = resolvedRoute.route.meta?.isAngular;

    if (isAngularRoute && !store.getters['AngularStore/isAngularLoaded']) {
      waitForAngularToLoad(payload, handleNavigation);
      return;
    }

    if (isAngularRoute) {
      handleAngularPath(cleanPath, mergedQueryParams);
    } else {
      pushIfRouteChanged({ ...options, path: normalizedPath, query: mergedQueryParams });
      store.dispatch('AngularStore/setIsNavigatingToAngular', false);
    }
  }
}
